import { DestroyRef, Directive, inject, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { ActivatedRoute, QueryParamsHandling, Router } from '@angular/router'
import {
  ResultOf,
  TypedDocumentNode,
  VariablesOf
} from '@graphql-typed-document-node/core'
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  Subject
} from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  skip as skipEmissions,
  takeUntil
} from 'rxjs/operators'
import { DataService } from '../data/providers/data.service'

import { QueryResult } from '../data/query-result'
import { ServerConfigService } from '../data/server-config'
import { DataTableFilterCollection } from '../providers/data-table/data-table-filter-collection'
import { DataTableSortCollection } from '../providers/data-table/data-table-sort-collection'
import { PermissionsService } from '../providers/permissions/permissions.service'
import {
  CustomFieldConfig,
  CustomFields,
  LanguageCode
} from './generated-types'
import { SelectionManager } from './utilities/selection-manager'

export type ListQueryFn<R> = (
  take: number,
  skip: number,
  ...args: any[]
) => QueryResult<R, any>
export type MappingFn<T, R> = (result: R) => { items: T[]; totalItems: number }
export type OnPageChangeFn<V> = (skip: number, take: number) => V

/**
 * Unwraps a query that returns a paginated list with an "items" property,
 * returning the type of one of the items in the array.
 */
export type ItemOf<T, K extends keyof T> = T[K] extends { items: infer R }
  ? R extends any[]
    ? R[number]
    : R
  : never

/**
 * @description
 * This is a base class which implements the logic required to fetch and manipulate
 * a list of data from a query which returns a PaginatedList type.
 *
 * It is normally used in combination with the {@link DataTable2Component}.
 *
 * @docsCategory list-detail-views
 */
@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class BaseListComponent<
    ResultType,
    ItemType,
    VariableType extends Record<string, any> = any
  >
  implements OnInit, OnDestroy
{
  searchTermControl = new FormControl('')
  selectionManager = new SelectionManager<any>({
    multiSelect: true,
    itemsAreEqual: (a, b) => a.id === b.id,
    additiveMode: true
  })
  result$: Observable<ResultType>
  items$: Observable<ItemType[]>
  totalItems$: Observable<number>
  itemsPerPage$: Observable<number>
  currentPage$: Observable<number>
  protected destroy$ = new Subject<void>()
  private listQuery: QueryResult<ResultType, VariableType>
  private listQueryFn: ListQueryFn<ResultType>
  private mappingFn: MappingFn<ItemType, ResultType>
  private onPageChangeFn: OnPageChangeFn<VariableType> = (skip, take) =>
    ({ options: { skip, take } }) as any
  protected refresh$ = new BehaviorSubject<undefined>(undefined)
  private defaults: { take: number; skip: number } = { take: 10, skip: 0 }
  private refreshTriggersInitialized = false
  private lastFetchKey: string | null = null
  private forceRefetch = false
  private ownRoutePath: string | null = null
  fetching$: Observable<boolean>

  constructor(
    protected router: Router,
    protected route: ActivatedRoute
  ) {}

  /**
   * @description
   * Sets the fetch function for the list being implemented.
   */
  setQueryFn(
    listQueryFn: ListQueryFn<ResultType>,
    mappingFn: MappingFn<ItemType, ResultType>,
    onPageChangeFn?: OnPageChangeFn<VariableType>,
    defaults?: { take: number; skip: number }
  ) {
    this.listQueryFn = listQueryFn
    this.mappingFn = mappingFn
    if (onPageChangeFn) {
      this.onPageChangeFn = onPageChangeFn
    }
    if (defaults) {
      this.defaults = defaults
    }
  }

  /** @internal */
  ngOnInit() {
    if (!this.listQueryFn) {
      throw new Error(
        `No listQueryFn has been defined. Please call super.setQueryFn() in the constructor.`
      )
    }
    this.ownRoutePath = this.currentRoutePath()
    const pageParam = this.route.snapshot.queryParamMap.get('page')
    const perPageParam = this.route.snapshot.queryParamMap.get('perPage')
    const initialPage = !pageParam ? 1 : +pageParam
    const initialTake = !perPageParam ? this.defaults.take : +perPageParam
    const initialSkip = (initialPage - 1) * initialTake

    this.listQuery = this.listQueryFn(initialTake, initialSkip)

    this.fetching$ = merge(
      of(true),
      this.listQuery.ref.valueChanges.pipe(map((result) => result.loading)),
      this.refresh$.pipe(map(() => true))
    ).pipe(distinctUntilChanged())

    this.lastFetchKey = this.buildFetchKeyFromSkipTake(initialSkip, initialTake)

    this.result$ = this.listQuery.stream$.pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    )
    // Apollo watchQuery là lazy: không có subscriber thì request đầu tiên không
    // bao giờ được gửi. Trước đây nó được kích gián tiếp bởi refresh() từ
    // contentLanguage$ — và với template chỉ render bảng sau khi hết `fetching`
    // thì `items$ | async` không bao giờ subscribe, list treo ở trạng thái
    // loading. Giữ một subscription tường minh để lần fetch đầu luôn chạy đúng
    // một lần; các `| async` trong template dùng chung qua shareReplay.
    this.result$.pipe(takeUntil(this.destroy$)).subscribe()
    this.items$ = this.result$.pipe(map((data) => this.mappingFn(data).items))
    this.totalItems$ = this.result$.pipe(
      map((data) => this.mappingFn(data).totalItems)
    )
    this.currentPage$ = this.route.queryParamMap.pipe(
      map((qpm) => qpm.get('page')),
      map((page) => (!page ? 1 : +page)),
      distinctUntilChanged()
    )
    this.itemsPerPage$ = this.route.queryParamMap.pipe(
      map((qpm) => qpm.get('perPage')),
      map((perPage) => (!perPage ? this.defaults.take : +perPage)),
      distinctUntilChanged()
    )

    combineLatest(this.currentPage$, this.itemsPerPage$, this.refresh$)
      .pipe(
        debounceTime(50),
        map(([currentPage, itemsPerPage]) => {
          const take = itemsPerPage
          const skip = (currentPage - 1) * itemsPerPage
          return {
            skip,
            take,
            fetchKey: this.buildFetchKeyFromSkipTake(skip, take),
            force: this.forceRefetch
          }
        }),
        distinctUntilChanged(
          (prev, curr) =>
            !curr.force &&
            prev.fetchKey === curr.fetchKey &&
            prev.skip === curr.skip &&
            prev.take === curr.take
        ),
        filter(
          ({ fetchKey, force }) => force || fetchKey !== this.lastFetchKey
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ skip, take, fetchKey }) => {
        if (this.destroy$.closed) {
          return
        }
        this.forceRefetch = false
        this.lastFetchKey = fetchKey
        this.listQuery.ref.refetch(this.onPageChangeFn(skip, take))
      })
  }

  /**
   * @description
   * Triggers a single list refetch after filter/sort changes.
   * Resets to page 1 via route when needed; otherwise refreshes in place.
   */
  protected triggerFilterRefresh() {
    const pageParam = this.route.snapshot.queryParamMap.get('page')
    if (pageParam && +pageParam !== 1) {
      this.setPageNumber(1)
      return
    }
    const perPageParam = this.route.snapshot.queryParamMap.get('perPage')
    const currentPage = !pageParam ? 1 : +pageParam
    const itemsPerPage = !perPageParam ? this.defaults.take : +perPageParam
    const skip = (currentPage - 1) * itemsPerPage
    const fetchKey = this.buildFetchKeyFromSkipTake(skip, itemsPerPage)
    if (fetchKey === this.lastFetchKey && !this.forceRefetch) {
      return
    }
    this.refresh$.next(undefined)
  }

  private buildFetchKeyFromSkipTake(skip: number, take: number): string {
    return JSON.stringify(
      this.normalizeForFetchKey(this.onPageChangeFn(skip, take))
    )
  }

  private normalizeForFetchKey(value: unknown): unknown {
    if (value === null || value === undefined) {
      return undefined
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeForFetchKey(item))
    }
    if (typeof value === 'object') {
      const normalized: Record<string, unknown> = {}
      for (const key of Object.keys(value as Record<string, unknown>).sort()) {
        const normalizedValue = this.normalizeForFetchKey(
          (value as Record<string, unknown>)[key]
        )
        if (normalizedValue !== undefined) {
          normalized[key] = normalizedValue
        }
      }
      return normalized
    }
    return value
  }

  /**
   * @description
   * Accepts a list of Observables which will trigger a refresh of the list when any of them emit.
   */
  protected refreshListOnChanges(...streams: Array<Observable<any>>) {
    const searchTerm$ = this.searchTermControl.valueChanges.pipe(
      filter(
        (value) => value !== null && (2 < value.length || value.length === 0)
      ),
      debounceTime(250)
    )

    const debouncedStreams = streams.map((stream) =>
      stream.pipe(debounceTime(300))
    )

    merge(searchTerm$, ...debouncedStreams)
      .pipe(debounceTime(50), takeUntil(this.destroy$))
      .subscribe(() => this.triggerFilterRefresh())
  }

  /** @internal */
  ngOnDestroy() {
    this.clearPaginationQueryParams()
    this.destroy$.next()
    this.destroy$.complete()
    this.listQuery?.destroy()
    this.lastFetchKey = null
  }

  private currentRoutePath(): string {
    return this.router.url.split('?')[0]
  }

  private clearPaginationQueryParams() {
    const queryParams = this.router.routerState.snapshot.root.queryParams
    if (!queryParams['page'] && !queryParams['perPage']) {
      return
    }
    const ownRoutePath = this.ownRoutePath
    setTimeout(() => {
      // Nếu vẫn còn ở đúng route cũ thì đây là một list anh em bị huỷ (đổi
      // mat-tab chẳng hạn), không phải rời trang. Xoá page/perPage lúc này sẽ
      // reset phân trang của list còn sống và kéo theo một lần refetch thừa.
      if (ownRoutePath && this.currentRoutePath() === ownRoutePath) {
        return
      }
      const current = this.router.routerState.snapshot.root.queryParams
      if (!current['page'] && !current['perPage']) {
        return
      }
      this.router.navigate([], {
        queryParams: { page: null, perPage: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
    }, 0)
  }

  /**
   * @description
   * Sets the current page number in the url.
   */
  setPageNumber(page: number) {
    const pageParam = this.route.snapshot.queryParamMap.get('page')
    const currentPage = !pageParam ? 1 : +pageParam
    if (currentPage === page) {
      return
    }
    if (page === 1) {
      this.setQueryParam('page', undefined, { replaceUrl: true })
    } else {
      this.setQueryParam('page', page, { replaceUrl: true })
    }
  }

  /**
   * @description
   * Sets the number of items per page in the url.
   */
  setItemsPerPage(perPage: number) {
    const perPageParam = this.route.snapshot.queryParamMap.get('perPage')
    const currentPerPage = !perPageParam ? this.defaults.take : +perPageParam
    if (currentPerPage === perPage) {
      return
    }
    this.setQueryParam(
      {
        perPage,
        page: undefined
      },
      { replaceUrl: true }
    )
  }

  /**
   * @description
   * Re-fetch the current page of results.
   */
  refresh() {
    this.forceRefetch = true
    this.refresh$.next(undefined)
  }

  protected setQueryParam(
    hash: { [key: string]: any },
    options?: {
      replaceUrl?: boolean
      queryParamsHandling?: QueryParamsHandling
    }
  )
  protected setQueryParam(
    key: string,
    value: any,
    options?: {
      replaceUrl?: boolean
      queryParamsHandling?: QueryParamsHandling
    }
  )
  protected setQueryParam(
    keyOrHash: string | { [key: string]: any },
    valueOrOptions?: any,
    maybeOptions?: {
      replaceUrl?: boolean
      queryParamsHandling?: QueryParamsHandling
    }
  ) {
    const options =
      (typeof keyOrHash === 'string' ? maybeOptions : valueOrOptions) ?? {}
    const queryParams =
      typeof keyOrHash === 'string'
        ? { [keyOrHash]: valueOrOptions }
        : keyOrHash
    const snapshot = this.route.snapshot.queryParamMap
    const hasChange = Object.entries(queryParams).some(([key, value]) => {
      return !this.queryParamEquals(snapshot.get(key), value)
    })
    if (!hasChange) {
      return
    }
    this.router.navigate(['./'], {
      queryParams,
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      ...options
    })
  }

  private queryParamEquals(
    current: string | null,
    next: string | number | null | undefined
  ): boolean {
    const normalizedCurrent = current ?? undefined
    const normalizedNext =
      next === null || next === undefined ? undefined : String(next)
    return normalizedCurrent === normalizedNext
  }
}

/**
 * @description
 * A version of the {@link BaseListComponent} which is designed to be used with a
 * [TypedDocumentNode](https://the-guild.dev/graphql/codegen/plugins/typescript/typed-document-node).
 *
 * @docsCategory list-detail-views
 */
@Directive()
export class TypedBaseListComponent<
    T extends TypedDocumentNode<any, Vars>,
    Field extends keyof ResultOf<T>,
    Vars extends { options: { filter: any; sort: any } } = VariablesOf<T>
  >
  extends BaseListComponent<
    ResultOf<T>,
    ItemOf<ResultOf<T>, Field>,
    VariablesOf<T>
  >
  implements OnInit
{
  availableLanguages$: Observable<LanguageCode[]>
  contentLanguage$: Observable<LanguageCode>

  protected dataService = inject(DataService)
  protected router = inject(Router)
  protected serverConfigService = inject(ServerConfigService)
  protected permissionsService = inject(PermissionsService)
  private refreshStreams: Array<Observable<any>> = []
  private collections: Array<
    DataTableFilterCollection | DataTableSortCollection<any>
  > = []
  constructor() {
    super(inject(Router), inject(ActivatedRoute))

    const destroyRef = inject(DestroyRef)
    destroyRef.onDestroy(() => {
      this.collections.forEach((c) => c.destroy())
    })
  }

  protected configure(config: {
    document: T
    getItems: (data: ResultOf<T>) => {
      items: Array<ItemOf<ResultOf<T>, Field>>
      totalItems: number
    }
    setVariables?: (skip: number, take: number) => VariablesOf<T>
    refreshListOnChanges?: Array<Observable<any>>
  }) {
    super.setQueryFn(
      (take, skip) => {
        const variables =
          config.setVariables?.(skip, take) ??
          ({ options: { skip, take } } as VariablesOf<T>)
        return this.dataService
          .query(config.document, variables, 'cache-first')
          .refetchOnChannelChange()
      },
      (data) => config.getItems(data),
      (skip, take) => config.setVariables?.(skip, take) ?? ({} as any)
    )
    this.availableLanguages$ = this.serverConfigService.getAvailableLanguages()
    // `uiState` chứa cả theme, locale, mainNavExpanded... nên stream này emit lại
    // mỗi khi bất kỳ state UI nào thay đổi. Chỉ giữ lại thay đổi thật của
    // contentLanguage, nếu không mỗi lần đổi theme là refetch toàn bộ list.
    this.contentLanguage$ = this.dataService.client
      .uiState()
      .mapStream(({ uiState }) => uiState.contentLanguage)
      .pipe(
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      )
    this.refreshStreams = config.refreshListOnChanges ?? []
  }

  ngOnInit() {
    super.ngOnInit()
    // `skipEmissions(1)` bỏ qua giá trị hiện tại lúc subscribe: ngOnInit đã fetch
    // trang đầu rồi, refetch thêm ở đây là request thừa trên mọi màn list.
    this.contentLanguage$
      .pipe(skipEmissions(1), takeUntil(this.destroy$))
      .subscribe(() => this.refresh())
    super.refreshListOnChanges(...this.refreshStreams)
  }

  createFilterCollection(): DataTableFilterCollection<
    NonNullable<NonNullable<Vars['options']>['filter']>
  > {
    const collection = new DataTableFilterCollection<
      NonNullable<Vars['options']['filter']>
    >(this.router)
    this.collections.push(collection)
    return collection
  }

  createSortCollection(): DataTableSortCollection<
    NonNullable<NonNullable<Vars['options']>['sort']>
  > {
    const collection = new DataTableSortCollection<
      NonNullable<Vars['options']['sort']>
    >(this.router)
    this.collections.push(collection)
    return collection
  }

  setLanguage(code: LanguageCode) {
    this.dataService.client.setContentLanguage(code).subscribe()
  }

  getCustomFieldConfig(
    key: Exclude<keyof CustomFields, '__typename'> | string
  ): CustomFieldConfig[] {
    return this.serverConfigService.getCustomFieldsFor(key).filter((f) => {
      if (f.requiresPermission?.length) {
        return this.permissionsService.userHasPermissions(f.requiresPermission)
      }
      return true
    })
  }
}
